import { useEffect, useState } from "react";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { PageLoader } from "@/components/ui/Spinner";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";
import { naturalSortKey } from "@/utils/naturalSort";
import { useAuthStore } from "@/store/auth";
import {
  useEmployeeExpenses,
  useCreateEmployeeExpense,
  useUpdateEmployeeExpense,
  useEmployeeExpenseProxyTargets,
  useDeleteEmployeeExpense,
  useApproveEmployeeExpense,
  useRejectEmployeeExpense,
  usePrepareTransferEmployeeExpense,
  useMarkPaidEmployeeExpense,
  useMarkBookedEmployeeExpense,
  useUndoLastEmployeeExpenseEvent,
  useUnlinkExpenseFromRecurringCost,
  useRecurringCosts,
  useAllJvInvoices,
  useJvAction,
} from "@/features/finances/hooks/useFinances";
import type { EmployeeExpense, JointVentureInvoice } from "@/features/finances/types";
import {
  useB2BInvoices,
  useMarkBookedB2BInvoice,
  useMarkPaidB2BInvoice,
  useMarkTransferredB2BInvoice,
} from "@/features/b2b_invoices/hooks/useB2BInvoices";
import { B2BInvoiceStatusBadge } from "@/features/b2b_invoices/components/B2BInvoiceStatusBadge";
import type { B2BInvoice } from "@/features/b2b_invoices/types";
import { MonthlyPeriodSelect, isPeriodInRange, formatRange } from "@/features/finances/components/MonthlyPeriodSelect";
import { Link, useSearchParams } from "react-router-dom";
import {
  EXPENSE_STATUS_LABELS,
  EXPENSE_STATUS_COLORS,
} from "@/features/finances/types";
import { MileageSheetModal } from "@/features/finances/components/MileageSheetModal";
import { ExpenseHistoryModal } from "@/features/finances/components/ExpenseHistoryModal";
import { LinkRecurringCostModal } from "@/features/finances/components/LinkRecurringCostModal";
import { Map } from "lucide-react";
import type { ExchangeRate } from "@/features/group_finance/api/groupFinance";

const EXPENSE_CURRENCIES = ["PLN", "EUR", "USD", "GBP", "CHF", "CZK", "NOK", "SEK", "DKK"] as const;

interface ProjectBrief {
  id: string;
  name: string;
  short_name: string;
}

const emptyForm = {
  expense_type: "invoice" as "invoice" | "mileage" | "proforma",
  description: "",
  amount: "",
  currency: "PLN",
  is_reimbursement: false,
  is_proforma: false,
  project: "",
};

const JV_STATUS_COLOR_MAP: Record<string, "gray" | "blue" | "orange" | "green" | "red" | "yellow"> = {
  draft: "yellow", pending: "gray", approved: "blue", transfer_prepared: "orange", paid: "green", rejected: "red",
};

/** Renders B2B employee invoices as rows inside a cost-invoice section.
 *  Kolorystyka celowo inna niż JV (turkus vs fiolet), żeby na jednej liście
 *  było widać z czym się ma do czynienia bez czytania etykiety. */
function B2bCostRows({
  invoices,
  actions,
}: {
  invoices: B2BInvoice[];
  actions: (inv: B2BInvoice) => import("react").ReactNode;
}) {
  if (invoices.length === 0) return null;
  return (
    <div className="overflow-x-auto mt-3 border-t border-dashed border-gray-200 dark:border-gray-700 pt-2">
      <table className="w-full text-sm">
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
          {invoices.map((inv) => (
            <tr key={inv.id} className="bg-teal-50/50 dark:bg-teal-900/10">
              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                <span className="inline-block text-[9px] px-1 py-px rounded bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 font-semibold mr-1">B2B</span>
                {inv.employee_full_name}
                <span className="block text-[10px] text-gray-400">
                  FV {inv.invoice_number || "—"} · {String(inv.period_month).padStart(2, "0")}.
                  {inv.period_year} ·{" "}
                  <Link to={`/b2b-invoices/${inv.id}`} className="text-[var(--color-primary)] hover:underline">szczegóły</Link>
                </span>
              </td>
              <td className="px-3 py-2 text-right font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap">
                {formatAmount(inv.amount_gross)} PLN
                <span className="block text-[10px] font-normal text-gray-400">brutto</span>
              </td>
              <td className="px-3 py-2"><B2BInvoiceStatusBadge status={inv.status} /></td>
              <td className="px-3 py-2 text-right whitespace-nowrap">{actions(inv)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Renders joint-venture partner cost invoices as rows inside a cost-invoice section,
 *  with the same per-section action buttons as employee expenses. */
function JvCostRows({ invoices, actions }: { invoices: JointVentureInvoice[]; actions: (jv: JointVentureInvoice) => import("react").ReactNode }) {
  if (invoices.length === 0) return null;
  return (
    <div className="overflow-x-auto mt-3 border-t border-dashed border-gray-200 dark:border-gray-700 pt-2">
      <table className="w-full text-sm">
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
          {invoices.map((jv) => (
            <tr key={jv.id} className="bg-purple-50/40 dark:bg-purple-900/10">
              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                <span className="inline-block text-[9px] px-1 py-px rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 font-semibold mr-1">JV</span>
                {jv.resolved_partner_name}
                <span className="block text-[10px] text-gray-400">
                  FV {jv.invoice_number || "—"} ·{" "}
                  <Link to={`/finances/invoices-prz/${jv.client_invoice}`} className="text-[var(--color-primary)] hover:underline">FV PRZ</Link>
                </span>
              </td>
              <td className="px-3 py-2 text-right font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap">
                {formatAmount(jv.amount_gross)} PLN
                <span className="block text-[10px] font-normal text-gray-400">brutto</span>
              </td>
              <td className="px-3 py-2"><Badge color={JV_STATUS_COLOR_MAP[jv.status]}>{jv.status_display}</Badge></td>
              <td className="px-3 py-2 text-right whitespace-nowrap">{actions(jv)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EmployeeExpensesPage() {
  const user = useAuthStore((s) => s.user);
  const isDirector = user?.role === "director";
  const isAccountant = user?.role === "accountant";
  const isCoordinator = user?.role === "coordinator";
  const isCeo = !!user?.is_ceo;
  const canManage = isDirector || isAccountant;
  const canBook = isAccountant || isCeo;
  // Who may file an expense on behalf of someone else (same pool as timesheets).
  const canProxy = isDirector || isAccountant || isCoordinator || isCeo;

  const { data: expenses = [], isLoading } = useEmployeeExpenses();

  // Deep-link z karty propozycji ARON: /expenses?expense=<id> → przewiń do wiersza tej faktury
  // i podświetl go. Działa wyłącznie na fakturach zwróconych przez (scoped serwerowo) listę,
  // więc id spoza zakresu użytkownika to bezpieczny no-op (brak wycieku danych).
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const targetExpenseId = searchParams.get("expense");
  // Efekt 1: kiedy docelowa faktura jest w załadowanej (scoped) liście — przewiń, podświetl
  // i zdejmij parametr z URL (żeby odświeżenie/nawigacja wstecz nie podświetlały ponownie).
  useEffect(() => {
    if (isLoading || !targetExpenseId) return;
    if (!expenses.some((e) => e.id === targetExpenseId)) return;
    const row = document.querySelector<HTMLElement>(
      `[data-expense-id="${targetExpenseId}"]`,
    );
    if (!row) return;
    row.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(targetExpenseId);
    setSearchParams({}, { replace: true });
  }, [isLoading, targetExpenseId, expenses, setSearchParams]);
  // Efekt 2: podświetlenie gaśnie po ~3 s (osobno, by wyczyszczenie parametru nie kasowało timera).
  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => setHighlightId(null), 3000);
    return () => clearTimeout(t);
  }, [highlightId]);

  const createMutation = useCreateEmployeeExpense();
  const updateMutation = useUpdateEmployeeExpense();
  const deleteMutation = useDeleteEmployeeExpense();
  const approveMutation = useApproveEmployeeExpense();
  const rejectMutation = useRejectEmployeeExpense();
  const prepareTransferMutation = usePrepareTransferEmployeeExpense();
  const markPaidMutation = useMarkPaidEmployeeExpense();
  const markBookedMutation = useMarkBookedEmployeeExpense();
  const undoLastMutation = useUndoLastEmployeeExpenseEvent();

  const handleUndoLast = (exp: EmployeeExpense) => {
    const label = exp.last_undoable_event?.event_type_display;
    if (
      confirm(
        `Cofnąć ostatnie zdarzenie${label ? `: „${label}”` : ""}? Faktura wróci do poprzedniego stanu.`,
      )
    ) {
      undoLastMutation.mutate(exp.id);
    }
  };

  const { data: projects = [] } = useQuery<ProjectBrief[]>({
    queryKey: ["projects"],
    queryFn: () =>
      apiClient
        .get<{ data: ProjectBrief[] }>("/projects/")
        .then((r: { data: { data: ProjectBrief[] } }) => r.data.data),
  });

  const { data: exchangeRates = [] } = useQuery<ExchangeRate[]>({
    queryKey: ["gf-exchange-rates"],
    queryFn: () =>
      apiClient
        .get<{ data: ExchangeRate[] }>("/group-finance/exchange-rates/")
        .then((r) => r.data.data),
    enabled: isCeo || isAccountant || !!user?.has_group_finance_access,
  });

  // Mileage sheet modal
  const [mileageOpen, setMileageOpen] = useState(false);

  const { data: proxyTargets = [] } = useEmployeeExpenseProxyTargets(canProxy);

  // Create form
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState<File | null>(null);
  const [formErr, setFormErr] = useState<string | null>(null);
  // On-behalf submission: checkbox + chosen target employee.
  const [proxyOn, setProxyOn] = useState(false);
  const [proxyEmployeeId, setProxyEmployeeId] = useState("");
  // Monthly recurring invoice mode: toggle + chosen recurring cost + period.
  const [isMonthly, setIsMonthly] = useState(false);
  const [recurringCostId, setRecurringCostId] = useState("");
  const [periodYear, setPeriodYear] = useState(new Date().getFullYear());
  const [periodMonth, setPeriodMonth] = useState(new Date().getMonth() + 1);

  // Recurring costs power the add-form dropdown (monthly-invoice mode). Only roles
  // with internal-cost access get a non-empty list (accountant/CEO/director).
  const { data: recurringCosts = [] } = useRecurringCosts();
  const activeRecurringCosts = recurringCosts.filter((c) => c.is_active);
  const selectedCost = activeRecurringCosts.find((c) => c.id === recurringCostId) ?? null;
  // Approve/reject modal (director)
  const [actionModal, setActionModal] = useState<{
    expense: EmployeeExpense;
    action: "approve" | "reject";
  } | null>(null);
  const [directorComment, setDirectorComment] = useState("");

  // Mark paid modal (accountant/director)
  const [paidModal, setPaidModal] = useState<{
    expense: EmployeeExpense;
  } | null>(null);
  const [paidAt, setPaidAt] = useState("");

  // History / undo modal
  const [historyExpense, setHistoryExpense] = useState<EmployeeExpense | null>(null);

  // Attaching an already-filed invoice to a recurring cost (faktura comiesięczna).
  // Same access as the recurring costs themselves — it rewrites their monthly figure.
  const canLinkRecurring = canManage || isCeo;
  const [linkModal, setLinkModal] = useState<EmployeeExpense | null>(null);
  const unlinkMutation = useUnlinkExpenseFromRecurringCost();
  const handleUnlinkRecurring = (exp: EmployeeExpense) => {
    const label = exp.recurring_cost_detail?.description ?? "kosztu stałego";
    if (
      confirm(
        `Odpiąć fakturę od „${label}”? Wróci do zwykłych faktur kosztowych` +
          `${exp.status !== "pending" && exp.status !== "rejected" ? ", a jej kwota wróci jako koszt jednorazowy" : ""}.`,
      )
    ) {
      unlinkMutation.mutate(exp.id);
    }
  };
  const recurringLinkProps = canLinkRecurring
    ? { onLinkRecurring: setLinkModal, onUnlinkRecurring: handleUnlinkRecurring }
    : {};

  // Edit modal — only pending (not-yet-accepted) expenses are editable.
  const [editModal, setEditModal] = useState<EmployeeExpense | null>(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editFile, setEditFile] = useState<File | null>(null);
  const [editErr, setEditErr] = useState<string | null>(null);

  const openEdit = (exp: EmployeeExpense) => {
    setEditForm({
      expense_type: exp.expense_type,
      description: exp.description,
      amount: exp.amount,
      currency: exp.currency,
      is_reimbursement: exp.is_reimbursement,
      is_proforma: exp.is_proforma,
      project: exp.project ?? "",
    });
    setEditFile(null);
    setEditErr(null);
    setEditModal(exp);
  };

  const handleEditSave = async () => {
    if (!editModal) return;
    // Monthly-invoice files inherit description/project/type from the recurring
    // cost — only amount and file can change.
    const isMonthlyExp = !!editModal.recurring_cost;
    if (!editForm.amount || (!isMonthlyExp && !editForm.description.trim())) {
      setEditErr("Opis i kwota są wymagane.");
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: editModal.id,
        data: isMonthlyExp
          ? { amount: editForm.amount, invoice_file: editFile }
          : {
              expense_type: editForm.expense_type,
              description: editForm.description.trim(),
              amount: editForm.amount,
              currency: editForm.expense_type === "mileage" ? "PLN" : editForm.currency,
              is_reimbursement: editForm.expense_type === "proforma" ? false : editForm.is_reimbursement,
              is_proforma: editForm.expense_type === "invoice" ? editForm.is_proforma : false,
              project: editForm.project || null,
              invoice_file: editFile,
            },
      });
      setEditModal(null);
    } catch {
      setEditErr("Błąd podczas zapisywania zmian.");
    }
  };

  const handleCreate = async () => {
    if (proxyOn && !proxyEmployeeId) {
      setFormErr("Wybierz pracownika, za którego składasz.");
      return;
    }
    if (isMonthly) {
      // Monthly recurring invoice: pick a recurring cost; description/project come
      // from the cost. Amount + month + file are per-invoice.
      if (!recurringCostId || !form.amount || !file) {
        setFormErr("Wybierz koszt stały, podaj kwotę i plik faktury.");
        return;
      }
      if (selectedCost && !isPeriodInRange(periodYear, periodMonth, selectedCost.start_date, selectedCost.end_date)) {
        setFormErr(`Miesiąc musi mieścić się w okresie kosztu (${formatRange(selectedCost.start_date, selectedCost.end_date)}).`);
        return;
      }
    } else if (!form.description.trim() || !form.amount || !file) {
      setFormErr("Opis, kwota i plik faktury są wymagane.");
      return;
    }
    try {
      if (isMonthly) {
        await createMutation.mutateAsync({
          recurring_cost: recurringCostId,
          amount: form.amount,
          period_year: periodYear,
          period_month: periodMonth,
          invoice_file: file!,
          employee: proxyOn && proxyEmployeeId ? proxyEmployeeId : null,
        });
      } else {
        await createMutation.mutateAsync({
          expense_type: form.expense_type,
          description: form.description.trim(),
          amount: form.amount,
          currency: form.expense_type === "mileage" ? "PLN" : form.currency,
          is_reimbursement: form.is_reimbursement,
          is_proforma: form.expense_type === "invoice" ? form.is_proforma : false,
          invoice_file: file!,
          project: form.project || null,
          employee: proxyOn && proxyEmployeeId ? proxyEmployeeId : null,
        });
      }
      setForm(emptyForm);
      setFile(null);
      setFormErr(null);
      setProxyOn(false);
      setProxyEmployeeId("");
      setIsMonthly(false);
      setRecurringCostId("");
    } catch {
      setFormErr("Błąd podczas zapisywania faktury.");
    }
  };

  const handleAction = async () => {
    if (!actionModal) return;
    if (actionModal.action === "approve") {
      await approveMutation.mutateAsync({
        id: actionModal.expense.id,
        comment: directorComment,
      });
    } else {
      await rejectMutation.mutateAsync({
        id: actionModal.expense.id,
        comment: directorComment,
      });
    }
    setActionModal(null);
    setDirectorComment("");
  };

  const handleMarkPaid = async () => {
    if (!paidModal || !paidAt) return;
    await markPaidMutation.mutateAsync({
      id: paidModal.expense.id,
      paid_at: paidAt,
    });
    setPaidModal(null);
    setPaidAt("");
  };

  // Split by status for sections
  // "My" = expenses I own plus those I filed on behalf of someone else.
  const myExpenses = expenses.filter(
    (e) => e.employee === user?.id || e.submitted_by === user?.id,
  );
  const myHasProxy = myExpenses.some(
    (e) => e.submitted_by === user?.id && e.employee !== user?.id,
  );
  const pending = expenses.filter((e) => e.status === "pending");
  const approved = expenses.filter((e) => e.status === "approved" && e.expense_type !== "proforma");
  const transferPrepared = expenses.filter((e) => e.status === "transfer_prepared");
  const toBook = expenses.filter((e) => !e.is_booked && e.status !== "rejected" && e.expense_type !== "proforma");

  // JV partner invoices flow through the SAME sections + actions as cost invoices.
  const { data: jvInvoices = [] } = useAllJvInvoices(!!user?.has_consortium_access);
  const jvAction = useJvAction();
  const jvPending = jvInvoices.filter((j) => j.status === "pending");
  const jvApproved = jvInvoices.filter((j) => j.status === "approved");
  const jvTransferPrepared = jvInvoices.filter((j) => j.status === "transfer_prepared");
  // Bookable only once accepted (approved/transfer_prepared/paid) and not yet booked —
  // booking a still-pending or rejected partner FV makes no sense.
  const jvToBook = jvInvoices.filter(
    (j) => !j.is_booked && j.status !== "pending" && j.status !== "rejected",
  );

  // Faktury B2B pracowników — po akceptacji CEO przechodzą tę samą ścieżkę
  // płatniczą co faktury kosztowe, więc pokazujemy je w sekcji „do opłacenia”.
  // Lista jest dostępna wyłącznie dla CEO i księgowości (backend: CanListB2BInvoices),
  // a wszystkie akcje księgowo-płatnicze wymagają tych samych ról.
  const canSeeB2B = isCeo || isAccountant;
  const { data: b2bResponse } = useB2BInvoices(undefined, canSeeB2B);
  const b2bMarkBooked = useMarkBookedB2BInvoice();
  const b2bMarkTransferred = useMarkTransferredB2BInvoice();
  const b2bMarkPaid = useMarkPaidB2BInvoice();
  // Zatwierdzona → zaksięgowana → przekazana do opłacenia → opłacona.
  // Wszystkie trzy stany pośrednie czekają na pieniądze, więc siedzą w jednej sekcji;
  // przycisk pokazuje kolejny krok właściwy dla statusu wiersza.
  const B2B_TO_PAY_STATUSES = ["approved", "booked", "transferred_for_payment"];
  const b2bToPay = (b2bResponse?.data ?? []).filter((i) =>
    B2B_TO_PAY_STATUSES.includes(i.status),
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6 px-3 py-4 sm:px-6 sm:py-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Faktury kosztowe
          </h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
        </div>
        <div className="pt-1">
          <Button variant="secondary" onClick={() => setMileageOpen(true)}>
            <Map size={15} className="mr-1.5" /> Generuj kilometrówkę
          </Button>
        </div>
      </div>


      {/* ── Add new expense ────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-800 p-5">
        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">
          Złóż fakturę kosztową
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-2">
              Rodzaj *
            </label>
            <div className="flex gap-4">
              {(["invoice", "mileage", "proforma"] as const).map((t) => (
                <label
                  key={t}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="expense_type"
                    value={t}
                    checked={form.expense_type === t}
                    onChange={() => {
                      setForm({ ...form, expense_type: t, ...(t === "proforma" ? { is_reimbursement: false } : {}) });
                      // Monthly recurring invoices apply to "Faktura" only.
                      if (t !== "invoice") setIsMonthly(false);
                    }}
                    className="accent-[var(--color-primary)]"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t === "invoice" ? "Faktura" : t === "mileage" ? "Kilometrówka" : "Proforma"}
                  </span>
                </label>
              ))}
            </div>
          </div>
          {form.expense_type === "invoice" && activeRecurringCosts.length > 0 && (
          <div className="sm:col-span-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isMonthly}
                onChange={(e) => {
                  setIsMonthly(e.target.checked);
                  if (e.target.checked) {
                    setForm((f) => ({ ...f, is_reimbursement: false }));
                  }
                }}
                className="w-4 h-4 accent-[var(--color-primary)]"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Faktura comiesięczna (powiązana z kosztem stałym)
              </span>
            </label>
          </div>
          )}
          {isMonthly && (
          <>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Koszt stały *
            </label>
            <SearchableSelect
              placeholder="— wybierz koszt stały —"
              value={recurringCostId}
              onChange={(e) => setRecurringCostId(e.target.value)}
              options={activeRecurringCosts.map((c) => ({
                value: c.id,
                label: c.company_name ? `${c.description} · ${c.company_name}` : c.description,
              }))}
            />
            {selectedCost && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Okres kosztu: {formatRange(selectedCost.start_date, selectedCost.end_date)}
                {selectedCost.project_name ? ` · ${selectedCost.project_name}` : ""}
              </p>
            )}
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Miesiąc *
            </label>
            <MonthlyPeriodSelect
              year={periodYear}
              month={periodMonth}
              onChange={(y, m) => { setPeriodYear(y); setPeriodMonth(m); }}
            />
            {selectedCost && !isPeriodInRange(periodYear, periodMonth, selectedCost.start_date, selectedCost.end_date) && (
              <p className="text-xs text-red-600 mt-1">
                Miesiąc poza okresem kosztu ({formatRange(selectedCost.start_date, selectedCost.end_date)}).
              </p>
            )}
          </div>
          </>
          )}
          {canProxy && proxyTargets.length > 0 && (
            <div className="sm:col-span-2 border-b border-gray-100 dark:border-gray-700 pb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={proxyOn}
                  onChange={(e) => {
                    setProxyOn(e.target.checked);
                    if (!e.target.checked) setProxyEmployeeId("");
                  }}
                  className="w-4 h-4 accent-[var(--color-primary)]"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Złóż za pracownika (w jego imieniu)
                </span>
              </label>
              {proxyOn && (
                <div className="mt-2">
                  <SearchableSelect
                    placeholder="— wybierz pracownika —"
                    value={proxyEmployeeId}
                    onChange={(e) => setProxyEmployeeId(e.target.value)}
                    options={proxyTargets.map((t) => ({
                      value: t.id,
                      label: t.is_active ? t.full_name : `${t.full_name} (nieaktywny)`,
                    }))}
                  />
                </div>
              )}
            </div>
          )}
          {/* Monthly invoices inherit przedsięwzięcie + opis from the recurring cost. */}
          {!isMonthly && (
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Przedsięwzięcie
            </label>
            <SearchableSelect
              placeholder="— bez przypisania —"
              value={form.project}
              onChange={(e) => setForm({ ...form, project: e.target.value })}
              options={[...projects]
                .sort((a, b) =>
                  naturalSortKey(`${a.short_name} — ${a.name}`).localeCompare(
                    naturalSortKey(`${b.short_name} — ${b.name}`),
                    "pl",
                  ),
                )
                .map((p: ProjectBrief) => ({
                  value: p.id,
                  label: `${p.short_name} — ${p.name}`,
                }))}
            />
          </div>
          )}
          {!isMonthly && (
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Opis *
            </label>
            <input
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              placeholder={
                form.expense_type === "mileage"
                  ? "Trasa, liczba km, stawka…"
                  : form.expense_type === "proforma"
                  ? "Czego dotyczy proforma"
                  : "Czego dotyczy faktura / wydatek"
              }
            />
          </div>
          )}
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
              {form.expense_type === "mileage"
                ? "Kwota zwrotu (PLN) *"
                : `Kwota brutto${!isMonthly && form.currency !== "PLN" ? ` (${form.currency})` : " (PLN)"} *`}
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                step="0.01"
                className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
              />
              {!isMonthly && form.expense_type !== "mileage" && (
                <select
                  className="border border-gray-300 rounded px-2 py-1.5 text-sm w-24"
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                >
                  {EXPENSE_CURRENCIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              )}
            </div>
            {form.expense_type === "mileage" && (
              <p className="text-xs text-gray-400 mt-1">
                Wpisz wyliczoną kwotę zwrotu (liczba km × stawka za km)
              </p>
            )}
          </div>
          {!isMonthly && form.expense_type !== "proforma" && (
            <div className="flex items-center gap-2 pt-5">
              <input
                type="checkbox"
                id="is_reimbursement"
                checked={form.is_reimbursement}
                onChange={(e) =>
                  setForm({ ...form, is_reimbursement: e.target.checked })
                }
                className="w-4 h-4 accent-[var(--color-primary)]"
              />
              <label
                htmlFor="is_reimbursement"
                className="text-sm text-gray-700 dark:text-gray-300"
              >
                Poproś o zwrot (zapłacone z własnych środków)
              </label>
            </div>
          )}
          {!isMonthly && form.expense_type === "invoice" && (
            <div className="sm:col-span-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_proforma"
                  checked={form.is_proforma}
                  onChange={(e) => setForm({ ...form, is_proforma: e.target.checked })}
                  className="w-4 h-4 accent-[var(--color-primary)]"
                />
                <label
                  htmlFor="is_proforma"
                  className="text-sm text-gray-700 dark:text-gray-300"
                >
                  To proforma opłacana z góry
                </label>
              </div>
              {form.is_proforma && (
                <p className="text-xs text-gray-400 mt-1">
                  Przejdzie normalną ścieżkę akceptacji i opłacenia, ale nie będzie liczona
                  jako koszt księgowy — właściwą fakturę złóż osobno, gdy dojdzie.
                </p>
              )}
            </div>
          )}
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
              {form.expense_type === "mileage" ? "Plik kilometrówki *" : "Plik faktury *"}
            </label>
            <FileDropZone
              files={file ? [file] : []}
              onFilesChange={(fs) => setFile(fs[0] ?? null)}
              accept=".pdf,.jpg,.jpeg,.png"
            />
          </div>
          {formErr && (
            <p className="sm:col-span-2 text-xs text-red-600">{formErr}</p>
          )}
          <div className="sm:col-span-2">
            <Button
              loading={createMutation.isPending}
              onClick={handleCreate}
              disabled={isMonthly
                ? (!recurringCostId || !form.amount || !file)
                : (!form.description || !form.amount || !file)}
            >
              {isMonthly
                ? "Dodaj fakturę comiesięczną"
                : form.expense_type === "mileage"
                ? "Złóż kilometrówkę"
                : "Złóż fakturę"}
            </Button>
          </div>
        </div>
      </div>


      {/* ── Director: pending to approve ──────────────────────────────── */}
      {isDirector && (
        <div className="bg-white dark:bg-gray-800 p-5">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Do akceptacji
            {pending.length > 0 && (
              <span className="ml-2 text-xs bg-orange-100 text-orange-700 rounded-full px-2 py-0.5">
                {pending.length}
              </span>
            )}
          </h2>
          {pending.length === 0 && jvPending.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Brak oczekujących faktur.
            </p>
          ) : (
            <>
            {pending.length > 0 && (
            <ExpenseTable
              exchangeRates={exchangeRates}
              highlightId={highlightId}
              expenses={pending}
              showEmployee
              onShowHistory={setHistoryExpense}
              {...recurringLinkProps}
              onUndoLast={handleUndoLast}
              actions={(exp) => (
                <div className="flex gap-1.5">
                  <button
                    onClick={() => {
                      setActionModal({ expense: exp, action: "approve" });
                      setDirectorComment("");
                    }}
                    className="text-xs text-green-700 hover:underline font-medium"
                  >
                    Akceptuj
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => {
                      setActionModal({ expense: exp, action: "reject" });
                      setDirectorComment("");
                    }}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Odrzuć
                  </button>
                </div>
              )}
            />
            )}
            <JvCostRows
              invoices={jvPending}
              actions={(jv) => (
                <div className="flex gap-1.5 justify-end">
                  <button onClick={() => jvAction.approve.mutate(jv.id)} className="text-xs text-green-700 hover:underline font-medium">Akceptuj</button>
                  <span className="text-gray-300">|</span>
                  <button onClick={() => jvAction.reject.mutate(jv.id)} className="text-xs text-red-600 hover:underline">Odrzuć</button>
                </div>
              )}
            />
            </>
          )}
        </div>
      )}

      {/* ── Accountant/Director: approved, to pay ─────────────────────── */}
      {canManage && (
        <div className="bg-white dark:bg-gray-800 p-5">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Zaakceptowane — do opłacenia
            {approved.length > 0 && (
              <span className="ml-2 text-xs bg-blue-100 text-[var(--color-primary)] rounded-full px-2 py-0.5">
                {approved.length}
              </span>
            )}
          </h2>
          {approved.length === 0 && jvApproved.length === 0 && b2bToPay.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Brak zaakceptowanych do opłacenia.
            </p>
          ) : (
            <>
            {approved.length > 0 && (
            <ExpenseTable
              exchangeRates={exchangeRates}
              highlightId={highlightId}
              expenses={approved}
              showEmployee
              onShowHistory={setHistoryExpense}
              {...recurringLinkProps}
              onUndoLast={handleUndoLast}
              actions={(exp) => (
                <div className="flex gap-2 items-center">
                  <button
                    onClick={() => {
                      setPaidModal({ expense: exp });
                      setPaidAt("");
                    }}
                    className="text-xs text-[var(--color-primary)] hover:underline font-medium"
                  >
                    Oznacz jako opłaconą
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => prepareTransferMutation.mutate(exp.id)}
                    disabled={prepareTransferMutation.isPending}
                    className="text-xs text-amber-600 hover:underline font-medium disabled:opacity-50"
                  >
                    Oznacz przygotowany przelew
                  </button>
                </div>
              )}
            />
            )}
            <JvCostRows
              invoices={jvApproved}
              actions={(jv) => (
                <div className="flex gap-2 items-center justify-end">
                  <button onClick={() => jvAction.markPaid.mutate({ id: jv.id })} className="text-xs text-[var(--color-primary)] hover:underline font-medium">Oznacz jako opłaconą</button>
                  <span className="text-gray-300">|</span>
                  <button onClick={() => jvAction.prepareTransfer.mutate(jv.id)} className="text-xs text-amber-600 hover:underline font-medium">Oznacz przygotowany przelew</button>
                </div>
              )}
            />
            <B2bCostRows
              invoices={b2bToPay}
              actions={(inv) =>
                inv.status === "approved" ? (
                  <button
                    onClick={() => b2bMarkBooked.mutate({ id: inv.id })}
                    disabled={b2bMarkBooked.isPending}
                    className="text-xs text-violet-700 hover:underline font-medium disabled:opacity-50"
                  >
                    Oznacz jako zaksięgowaną
                  </button>
                ) : inv.status === "booked" ? (
                  <button
                    onClick={() => b2bMarkTransferred.mutate({ id: inv.id })}
                    disabled={b2bMarkTransferred.isPending}
                    className="text-xs text-amber-600 hover:underline font-medium disabled:opacity-50"
                  >
                    Oznacz jako przekazaną do opłacenia
                  </button>
                ) : (
                  <button
                    onClick={() => b2bMarkPaid.mutate({ id: inv.id })}
                    disabled={b2bMarkPaid.isPending}
                    className="text-xs text-[var(--color-primary)] hover:underline font-medium disabled:opacity-50"
                  >
                    Oznacz jako opłaconą
                  </button>
                )
              }
            />
            </>
          )}
        </div>
      )}

      {/* ── Accountant/Director: transfer prepared ────────────────────── */}
      {canManage && (
        <div className="bg-white dark:bg-gray-800 p-5">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Przelew przygotowany
            {transferPrepared.length > 0 && (
              <span className="ml-2 text-xs bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">
                {transferPrepared.length}
              </span>
            )}
          </h2>
          {transferPrepared.length === 0 && jvTransferPrepared.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Brak faktur z przygotowanym przelewem.
            </p>
          ) : (
            <>
            {transferPrepared.length > 0 && (
            <ExpenseTable
              exchangeRates={exchangeRates}
              highlightId={highlightId}
              expenses={transferPrepared}
              showEmployee
              onShowHistory={setHistoryExpense}
              {...recurringLinkProps}
              onUndoLast={handleUndoLast}
              actions={(exp) => (
                <button
                  onClick={() => {
                    setPaidModal({ expense: exp });
                    setPaidAt("");
                  }}
                  className="text-xs text-[var(--color-primary)] hover:underline font-medium"
                >
                  Oznacz jako opłaconą
                </button>
              )}
            />
            )}
            <JvCostRows
              invoices={jvTransferPrepared}
              actions={(jv) => (
                <button onClick={() => jvAction.markPaid.mutate({ id: jv.id })} className="text-xs text-[var(--color-primary)] hover:underline font-medium">Oznacz jako opłaconą</button>
              )}
            />
            </>
          )}
        </div>
      )}

      {/* ── Accountant/CEO: paid, to book ────────────────────────────── */}
      {canBook && (
        <div className="bg-white dark:bg-gray-800 p-5">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Do zaksięgowania
            {toBook.length > 0 && (
              <span className="ml-2 text-xs bg-violet-100 text-violet-700 rounded-full px-2 py-0.5">
                {toBook.length}
              </span>
            )}
          </h2>
          {toBook.length === 0 && jvToBook.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Brak opłaconych faktur do zaksięgowania.
            </p>
          ) : (
            <>
            {toBook.length > 0 && (
            <ExpenseTable
              exchangeRates={exchangeRates}
              highlightId={highlightId}
              expenses={toBook}
              showEmployee
              onShowHistory={setHistoryExpense}
              {...recurringLinkProps}
              onUndoLast={handleUndoLast}
              actions={(exp) => (
                <button
                  onClick={() => markBookedMutation.mutate(exp.id)}
                  disabled={markBookedMutation.isPending}
                  className="text-xs text-violet-700 hover:underline font-medium disabled:opacity-50 text-left"
                >
                  {exp.is_proforma ? "Przyjęta, nieksięgowana — proforma" : "Oznacz jako zaksięgowaną"}
                </button>
              )}
            />
            )}
            <JvCostRows
              invoices={jvToBook}
              actions={(jv) => (
                <button onClick={() => jvAction.markBooked.mutate(jv.id)} className="text-xs text-violet-700 hover:underline font-medium">Oznacz jako zaksięgowaną</button>
              )}
            />
            </>
          )}
        </div>
      )}

      {/* ── My submissions ────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-800 p-5">
        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">
          Moje faktury kosztowe
        </h2>
        {isLoading ? (
          <PageLoader />
        ) : myExpenses.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Nie złożyłeś jeszcze żadnych faktur.
          </p>
        ) : (
          <ExpenseTable
            exchangeRates={exchangeRates}
            highlightId={highlightId}
            expenses={myExpenses}
            showEmployee={myHasProxy}
            onShowHistory={setHistoryExpense}
              {...recurringLinkProps}
            onUndoLast={handleUndoLast}
            actions={(exp) =>
              exp.status === "pending" || isCeo ? (
                <>
                  {exp.status === "pending" && (
                    <button
                      onClick={() => openEdit(exp)}
                      className="text-xs text-[var(--color-primary)] hover:underline font-medium"
                    >
                      Edytuj
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (confirm("Usunąć tę fakturę?"))
                        deleteMutation.mutate(exp.id);
                    }}
                    disabled={deleteMutation.isPending}
                    className="text-xs text-red-500 hover:underline disabled:opacity-50"
                  >
                    Usuń
                  </button>
                </>
              ) : null
            }
          />
        )}
      </div>

      {/* ── Director/Accountant/CEO: full history ────────────────────── */}
      {(canManage || isCeo) && (
        <div className="bg-white dark:bg-gray-800 p-5">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Historia — wszystkie faktury
          </h2>
          {isLoading ? (
            <PageLoader />
          ) : (() => {
            const historyExpenses = isCeo
              ? expenses
              : expenses.filter((e) => e.status === "paid" || e.status === "rejected" || (e.status === "approved" && e.expense_type === "proforma"));
            return historyExpenses.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Brak historycznych.
              </p>
            ) : (
              <ExpenseTable
                exchangeRates={exchangeRates}
                highlightId={highlightId}
                expenses={historyExpenses}
                showEmployee
                onShowHistory={setHistoryExpense}
              {...recurringLinkProps}
                onUndoLast={handleUndoLast}
                actions={(canBook || isCeo) ? (exp) => {
                  // Booked status now shows under the Status badge; un-booking is done
                  // only via "Cofnij ostatnie zdarzenie" when booking is the last event.
                  const canBookThis =
                    canBook && !exp.is_booked && exp.status !== "rejected" && exp.expense_type !== "proforma";
                  if (!canBookThis && !isCeo) return null;
                  return (
                    <div className="flex flex-col items-start gap-3">
                      {canBookThis && (
                        <button
                          onClick={() => markBookedMutation.mutate(exp.id)}
                          disabled={markBookedMutation.isPending}
                          className="text-xs text-violet-700 hover:underline font-medium disabled:opacity-50 text-left"
                        >
                          {exp.is_proforma ? "Przyjęta, nieksięgowana — proforma" : "Oznacz jako zaksięgowaną"}
                        </button>
                      )}
                      {isCeo && (
                        <button
                          onClick={() => { if (confirm("Usunąć tę fakturę?")) deleteMutation.mutate(exp.id); }}
                          disabled={deleteMutation.isPending}
                          className="text-xs text-red-500 hover:underline disabled:opacity-50"
                        >
                          Usuń
                        </button>
                      )}
                    </div>
                  );
                } : undefined}
              />
            );
          })()}
        </div>
      )}

      {/* ── Approve / Reject modal ────────────────────────────────────── */}
      {actionModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
              {actionModal.action === "approve"
                ? "Akceptuj fakturę"
                : "Odrzuć fakturę"}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              {actionModal.expense.employee_name} ·{" "}
              {formatAmount(actionModal.expense.amount)} PLN ·{" "}
              {actionModal.expense.description}
            </p>
            <div className="flex flex-col gap-3">
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Komentarz (opcjonalny)
                </label>
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  value={directorComment}
                  onChange={(e) => setDirectorComment(e.target.value)}
                  placeholder={
                    actionModal.action === "reject"
                      ? "Powód odrzucenia…"
                      : "Opcjonalna uwaga"
                  }
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  loading={
                    approveMutation.isPending || rejectMutation.isPending
                  }
                  onClick={handleAction}
                  className={
                    actionModal.action === "reject"
                      ? "bg-red-600 hover:bg-red-700"
                      : undefined
                  }
                >
                  {actionModal.action === "approve" ? "Zaakceptuj" : "Odrzuć"}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setActionModal(null)}
                >
                  Anuluj
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit modal (pending expenses only) ────────────────────────── */}
      {editModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Edytuj fakturę
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              {editModal.recurring_cost
                ? `Faktura comiesięczna${editModal.recurring_cost_detail ? ` · ${editModal.recurring_cost_detail.description}` : ""}${editModal.period_label ? ` · ${editModal.period_label}` : ""} — zmienić można kwotę i plik.`
                : "Edycja możliwa tylko do momentu akceptacji."}
            </p>
            <div className="flex flex-col gap-3">
              {!editModal.recurring_cost && (
                <>
                  <div>
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-2">
                      Rodzaj *
                    </label>
                    <div className="flex gap-4">
                      {(["invoice", "mileage", "proforma"] as const).map((t) => (
                        <label key={t} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="edit_expense_type"
                            value={t}
                            checked={editForm.expense_type === t}
                            onChange={() =>
                              setEditForm({ ...editForm, expense_type: t, ...(t === "proforma" ? { is_reimbursement: false } : {}) })
                            }
                            className="accent-[var(--color-primary)]"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {t === "invoice" ? "Faktura" : t === "mileage" ? "Kilometrówka" : "Proforma"}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                      Przedsięwzięcie
                    </label>
                    <SearchableSelect
                      placeholder="— bez przypisania —"
                      value={editForm.project}
                      onChange={(e) => setEditForm({ ...editForm, project: e.target.value })}
                      options={[...projects]
                        .sort((a, b) =>
                          naturalSortKey(`${a.short_name} — ${a.name}`).localeCompare(
                            naturalSortKey(`${b.short_name} — ${b.name}`),
                            "pl",
                          ),
                        )
                        .map((p: ProjectBrief) => ({
                          value: p.id,
                          label: `${p.short_name} — ${p.name}`,
                        }))}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                      Opis *
                    </label>
                    <input
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    />
                  </div>
                </>
              )}
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  {editForm.expense_type === "mileage"
                    ? "Kwota zwrotu (PLN) *"
                    : `Kwota brutto${!editModal.recurring_cost && editForm.currency !== "PLN" ? ` (${editForm.currency})` : " (PLN)"} *`}
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.01"
                    className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
                    value={editForm.amount}
                    onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                  />
                  {!editModal.recurring_cost && editForm.expense_type !== "mileage" && (
                    <select
                      className="border border-gray-300 rounded px-2 py-1.5 text-sm w-24"
                      value={editForm.currency}
                      onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                    >
                      {EXPENSE_CURRENCIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              {!editModal.recurring_cost && editForm.expense_type !== "proforma" && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit_is_reimbursement"
                    checked={editForm.is_reimbursement}
                    onChange={(e) => setEditForm({ ...editForm, is_reimbursement: e.target.checked })}
                    className="w-4 h-4 accent-[var(--color-primary)]"
                  />
                  <label
                    htmlFor="edit_is_reimbursement"
                    className="text-sm text-gray-700 dark:text-gray-300"
                  >
                    Poproś o zwrot (zapłacone z własnych środków)
                  </label>
                </div>
              )}
              {!editModal.recurring_cost && editForm.expense_type === "invoice" && (
                <div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="edit_is_proforma"
                      checked={editForm.is_proforma}
                      onChange={(e) => setEditForm({ ...editForm, is_proforma: e.target.checked })}
                      className="w-4 h-4 accent-[var(--color-primary)]"
                    />
                    <label
                      htmlFor="edit_is_proforma"
                      className="text-sm text-gray-700 dark:text-gray-300"
                    >
                      To proforma opłacana z góry
                    </label>
                  </div>
                  {editForm.is_proforma && (
                    <p className="text-xs text-gray-400 mt-1">
                      Nie będzie liczona jako koszt księgowy — właściwą fakturę złóż osobno, gdy dojdzie.
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Nowy plik (opcjonalnie — pusty zachowa obecny)
                </label>
                <FileDropZone
                  files={editFile ? [editFile] : []}
                  onFilesChange={(fs) => setEditFile(fs[0] ?? null)}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                {editModal.invoice_managed_file_detail?.url && !editFile && (
                  <p className="text-xs text-gray-400 mt-1">
                    Obecny plik:{" "}
                    <a
                      href={editModal.invoice_managed_file_detail.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[var(--color-primary)] hover:underline"
                    >
                      {editModal.invoice_managed_file_detail.original_filename ?? "pobierz"}
                    </a>
                  </p>
                )}
              </div>
              {editErr && <p className="text-xs text-red-600">{editErr}</p>}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  loading={updateMutation.isPending}
                  onClick={handleEditSave}
                  disabled={!editForm.amount || (!editModal.recurring_cost && !editForm.description.trim())}
                >
                  Zapisz zmiany
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setEditModal(null)}>
                  Anuluj
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Mileage sheet modal ───────────────────────────────────────── */}
      <MileageSheetModal open={mileageOpen} onClose={() => setMileageOpen(false)} />

      {/* ── History modal (read-only) ─────────────────────────────────── */}
      {historyExpense && (
        <ExpenseHistoryModal
          expense={historyExpense}
          onClose={() => setHistoryExpense(null)}
        />
      )}

      {/* ── Attach an existing invoice to a recurring cost ─────────────── */}
      {linkModal && (
        <LinkRecurringCostModal
          expense={linkModal}
          onClose={() => setLinkModal(null)}
        />
      )}

      {/* ── Mark paid modal ───────────────────────────────────────────── */}
      {paidModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Oznacz jako opłaconą
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              {paidModal.expense.employee_name} ·{" "}
              {formatAmount(paidModal.expense.amount)} PLN ·{" "}
              {paidModal.expense.description}
            </p>
            <div className="flex flex-col gap-3">
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Data wypłaty *
                </label>
                <DatePicker
                  value={paidAt}
                  onChange={setPaidAt}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  loading={markPaidMutation.isPending}
                  onClick={handleMarkPaid}
                  disabled={!paidAt}
                >
                  Zapisz
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPaidModal(null)}
                >
                  Anuluj
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shared table component ────────────────────────────────────────────────

interface ExpenseTableProps {
  expenses: EmployeeExpense[];
  showEmployee?: boolean;
  actions?: (exp: EmployeeExpense) => React.ReactNode;
  onShowHistory?: (exp: EmployeeExpense) => void;
  onUndoLast?: (exp: EmployeeExpense) => void;
  /** Attach an already-filed invoice to a recurring cost / detach it again.
   *  Passed only for roles with internal-cost access (backend enforces the same). */
  onLinkRecurring?: (exp: EmployeeExpense) => void;
  onUnlinkRecurring?: (exp: EmployeeExpense) => void;
  exchangeRates?: ExchangeRate[];
  /** Id faktury do podświetlenia (deep-link z ARON: /expenses?expense=<id>). */
  highlightId?: string | null;
}

function plnEquivalent(exp: EmployeeExpense, rates: ExchangeRate[]): string | null {
  if (exp.currency === "PLN") return null;
  if (exp.status === "paid" && exp.amount_pln != null) return exp.amount_pln;
  const rate = rates.find((r) => r.currency === exp.currency);
  if (!rate) return null;
  return (parseFloat(exp.amount) * parseFloat(rate.rate)).toFixed(2);
}

function ExpenseTable({ expenses, showEmployee, actions, onShowHistory, onUndoLast, onLinkRecurring, onUnlinkRecurring, exchangeRates = [], highlightId }: ExpenseTableProps) {
  const hasActionsCol = !!actions || !!onShowHistory || !!onUndoLast || !!onLinkRecurring;
  const renderActions = (exp: EmployeeExpense) => {
    const custom = actions?.(exp);
    const showUndo = !!onUndoLast && !!exp.last_undoable_event?.can_undo;
    // Only a real, non-rejected invoice can become a monthly file — mirrors the
    // server-side guards, so the button never offers a guaranteed 400.
    // Mirrors the server's _assert_linkable and nothing more: reimbursements DO
    // link (a fixed cost paid privately is still a zwrot), and a foreign currency
    // is converted at the NBP rate when attaching — neither is a guard here.
    const showLink =
      !!onLinkRecurring
      && !exp.recurring_cost
      && exp.expense_type === "invoice"
      && !exp.is_proforma
      && exp.status !== "rejected";
    const showUnlink = !!onUnlinkRecurring && !!exp.recurring_cost;
    if (!custom && !onShowHistory && !showUndo && !showLink && !showUnlink) return null;
    return (
      <div className="flex flex-col items-start gap-1.5">
        {custom}
        {showLink && (
          <button
            onClick={() => onLinkRecurring!(exp)}
            className="text-xs text-sky-700 dark:text-sky-400 hover:underline"
          >
            Podepnij pod koszt stały
          </button>
        )}
        {showUnlink && (
          <button
            onClick={() => onUnlinkRecurring!(exp)}
            className="text-xs text-sky-700 dark:text-sky-400 hover:underline"
          >
            Odepnij od kosztu stałego
          </button>
        )}
        {showUndo && (
          <button
            onClick={() => onUndoLast!(exp)}
            className="text-xs text-rose-600 hover:text-rose-700 hover:underline font-medium"
          >
            Cofnij ostatnie zdarzenie
          </button>
        )}
        {onShowHistory && (
          <button
            onClick={() => onShowHistory(exp)}
            className="text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:underline"
          >
            Historia
          </button>
        )}
      </div>
    );
  };
  return (
    <>
      {/* Mobile cards */}
      <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700">
        {expenses.map((exp) => (
          <div
            key={exp.id}
            data-expense-id={exp.id}
            className={`py-3 transition-colors ${exp.id === highlightId ? "ring-2 ring-[var(--color-primary)] rounded-lg bg-blue-50/60 dark:bg-blue-900/20" : ""}`}
          >
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                  <span
                    className={`text-xs font-medium px-1.5 py-0.5 rounded ${exp.expense_type === "mileage" ? "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" : exp.expense_type === "proforma" ? "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"}`}
                  >
                    {exp.expense_type === "mileage" ? "Kilometrówka" : exp.expense_type === "proforma" ? "Proforma" : "Faktura"}
                  </span>
                  {exp.is_proforma && (
                    <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                      Proforma
                    </span>
                  )}
                  {exp.is_reimbursement && (
                    <span className="text-xs text-[var(--color-primary)] dark:text-blue-400 font-medium">
                      zwrot
                    </span>
                  )}
                  <Badge color={EXPENSE_STATUS_COLORS[exp.status]}>
                    {EXPENSE_STATUS_LABELS[exp.status]}
                  </Badge>
                  {exp.is_booked && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                      {exp.is_proforma ? "Przyjęta, nieksięgowana — proforma" : "Zaksięgowana"}
                    </span>
                  )}
                  {exp.recurring_cost && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300">
                      Comiesięczna{exp.recurring_cost_detail ? ` · ${exp.recurring_cost_detail.description}` : ""}{exp.period_label ? ` · ${exp.period_label}` : ""}
                    </span>
                  )}
                </div>
                {showEmployee && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                    {exp.employee_name}
                    {exp.submitted_by_name && (
                      <span className="block text-[11px] text-gray-400 dark:text-gray-500 italic">
                        złożone przez {exp.submitted_by_name}
                      </span>
                    )}
                  </p>
                )}
                <p className="text-sm text-gray-800 dark:text-gray-200 leading-snug">
                  {exp.description}
                </p>
                {exp.project_name && (
                  <p className="text-xs text-[var(--color-primary)] dark:text-indigo-400 mt-0.5 break-words leading-snug">
                    {exp.project_name}
                  </p>
                )}
                {exp.director_comment && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 italic">
                    &ldquo;{exp.director_comment}&rdquo;
                  </p>
                )}
                {exp.paid_at && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    Opłacono: {formatDate(exp.paid_at)} przez {exp.paid_by_name}
                  </p>
                )}
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  {new Date(exp.created_at).toLocaleDateString("pl-PL")}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="font-semibold text-sm text-gray-900 dark:text-gray-100 tabular-nums whitespace-nowrap">
                  {formatAmount(exp.amount)} {exp.currency}
                </p>
                {exp.expense_type === "invoice" && (
                  <p className="text-[10px] text-gray-400 dark:text-gray-500">
                    brutto
                  </p>
                )}
                {exp.currency !== "PLN" && (() => {
                  const pln = plnEquivalent(exp, exchangeRates);
                  if (!pln) return null;
                  return (
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
                      {exp.status === "paid" ? `(${formatAmount(pln)} PLN)` : `≈ ${formatAmount(pln)} PLN`}
                    </p>
                  );
                })()}
                {exp.invoice_managed_file_detail?.url ? (
                  <a
                    href={exp.invoice_managed_file_detail.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-[var(--color-primary)] hover:underline mt-1 block"
                  >
                    Pobierz
                  </a>
                ) : null}
              </div>
            </div>
            {hasActionsCol && (
              <div className="mt-2">{renderActions(exp)}</div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-xs min-w-[520px]">
          <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
            <tr>
              {showEmployee && (
                <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Pracownik
                </th>
              )}
              <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Opis
              </th>
              <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Kwota
              </th>
              <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Status
              </th>
              <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Data złożenia
              </th>
              <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Plik
              </th>
              {hasActionsCol && <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {expenses.map((exp) => (
              <tr
                key={exp.id}
                data-expense-id={exp.id}
                className={`transition-colors ${exp.id === highlightId ? "bg-blue-50 dark:bg-blue-900/20 outline outline-2 outline-[var(--color-primary)]" : "hover:bg-gray-50 dark:hover:bg-gray-700/40"}`}
              >
                {showEmployee && (
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300 text-xs">
                    {exp.employee_name}
                    {exp.submitted_by_name && (
                      <span className="block text-[11px] text-gray-400 dark:text-gray-500 italic">
                        złożone przez {exp.submitted_by_name}
                      </span>
                    )}
                  </td>
                )}
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span
                      className={`text-xs font-medium px-1.5 py-0.5 rounded ${exp.expense_type === "mileage" ? "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" : exp.expense_type === "proforma" ? "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"}`}
                    >
                      {exp.expense_type === "mileage" ? "Kilometrówka" : exp.expense_type === "proforma" ? "Proforma" : "Faktura"}
                    </span>
                    {exp.is_proforma && (
                      <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                        Proforma
                      </span>
                    )}
                    {exp.is_reimbursement && (
                      <span className="text-xs text-[var(--color-primary)] dark:text-blue-400">
                        zwrot
                      </span>
                    )}
                    {exp.recurring_cost && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300">
                        Comiesięczna{exp.recurring_cost_detail ? ` · ${exp.recurring_cost_detail.description}` : ""}{exp.period_label ? ` · ${exp.period_label}` : ""}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-800 dark:text-gray-200">
                    {exp.description}
                  </p>
                  {exp.project_name ? (
                    <p className="text-xs text-[var(--color-primary)] dark:text-indigo-400 mt-0.5">
                      {exp.project_name}
                    </p>
                  ) : null}
                  {exp.director_comment && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 italic">
                      &ldquo;{exp.director_comment}&rdquo;
                    </p>
                  )}
                  {exp.paid_at && (
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      Opłacono: {formatDate(exp.paid_at)} przez {exp.paid_by_name}
                    </p>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                  {formatAmount(exp.amount)} {exp.currency}
                  {exp.expense_type === "invoice" && (
                    <div className="text-xs font-normal text-gray-400">brutto</div>
                  )}
                  {exp.currency !== "PLN" && (() => {
                    const pln = plnEquivalent(exp, exchangeRates);
                    if (!pln) return null;
                    return (
                      <div className="text-xs font-normal text-gray-400 tabular-nums">
                        {exp.status === "paid" ? `(${formatAmount(pln)} PLN)` : `≈ ${formatAmount(pln)} PLN`}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-3 py-2">
                  <Badge color={EXPENSE_STATUS_COLORS[exp.status]}>
                    {EXPENSE_STATUS_LABELS[exp.status]}
                  </Badge>
                  {exp.is_booked && (
                    <div className="mt-1">
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                        {exp.is_proforma ? "Przyjęta, nieksięgowana — proforma" : "Zaksięgowana"}
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {new Date(exp.created_at).toLocaleDateString("pl-PL")}
                </td>
                <td className="px-3 py-2">
                  {exp.invoice_managed_file_detail?.url ? (
                    <a
                      href={exp.invoice_managed_file_detail.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-[var(--color-primary)] hover:underline"
                    >
                      Pobierz
                    </a>
                  ) : (
                    <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                  )}
                </td>
                {hasActionsCol && <td className="px-3 py-2 text-left">{renderActions(exp)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
